'use client';

import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useExternalLinkGuard } from '../../ui/ExternalLinkGuard';
import {
  UniswapIcon,
  DexscreenerIcon,
  GlobeIcon,
} from '../../ui/BrandIcons';
import {
  ADAPTER_BRAND,
  ADAPTER_KIND_LABEL,
  type AdapterLink,
  type LobbyAdapter,
  type LobbyAdapterStatus,
} from '../../data/mock-adapters';

const STATUS_PILL: Record<LobbyAdapterStatus, { label: string; hue: string }> = {
  live: { label: 'Live', hue: '#39d79e' },
  paused: { label: 'Paused', hue: '#facc15' },
  cooldown: { label: 'Cooldown', hue: '#f97316' },
};

/**
 * Shared chrome for every per-adapter panel.
 *
 * Each panel kind (Flaunch / Uniswap v3 / Uniswap v4 / Aerodrome / Custom)
 * fills `children` with its own metric grid; this shell provides the
 * brand-coloured header, status pill, default subtitle, and the link row.
 *
 * The whole adapter stack is intentionally rendered as plain cards rather
 * than nested GamePanels — stacking GamePanels would multiply the
 * corner-bracket chrome and read as visual noise. The outer wrapper is
 * still a `DraggableFloatingPanel` so users can move the entire adapter
 * column as a unit (see `LobbyClient`'s yield slot).
 */
interface AdapterPanelShellProps {
  adapter: LobbyAdapter;
  /** Adapter-specific metric grid (typically 2-4 Stat cells). */
  children: ReactNode;
  /** Optional row stacked under metrics (e.g. protocol collect on
   *  boardroom, or per-card creator yield actions). */
  belowStats?: ReactNode;
}

export function AdapterPanelShell({ adapter, children, belowStats }: AdapterPanelShellProps) {
  const brand = ADAPTER_BRAND[adapter.kind];
  const fallbackLabel = ADAPTER_KIND_LABEL[adapter.kind];
  const promo = adapter.promo;
  const headline = (promo?.title?.trim() || adapter.label || fallbackLabel).trim();
  // Tag each chip's source so the click handler can pick the right
  // guard. `adapter.links` are protocol-built (basescan / flaunch.gg
  // etc.) and use the trusted-host fast-path; `promo.extraLinks` are
  // creator-authored (Site / Telegram / Discord) and always show the
  // DYOR interstitial regardless of host.
  const outboundLinks: Array<{ link: AdapterLink; isMetadata: boolean }> = [
    ...adapter.links.map((link) => ({ link, isMetadata: false })),
    ...(promo?.extraLinks ?? []).map((link) => ({ link, isMetadata: true })),
  ];

  return (
    <div
      // `data-hud-card` lets the bright-skybox CSS in `lobby.module.css`
      // bump this adapter card's opacity when bright hubs (Open Sky / Homestead) are on so
      // stat labels stay legible against a pale backdrop.
      data-hud-card
      className="rounded-[5px] bg-black/45 border border-amber-500/20 backdrop-blur-md shadow-[inset_0_0_0_1px_rgba(255,215,120,0.05),0_10px_30px_-20px_rgba(0,0,0,0.9)]"
      style={{ borderLeft: `2px solid ${brand.hue}88` }}
    >
      {/* Tightened header: inline brand bullet + single-row title line
          instead of the old 28×28 framed icon + two-line stack. The
          subtitle drops to 9px zinc to recede behind the adapter label
          and the overall card height shrinks by ~14px without losing
          any information. */}
      <header className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-amber-500/10">
        {/* Custom adapters use a single avatar slot — uploaded image
            when the creator attached one, otherwise a brand-tinted
            globe placeholder at the same 28×28 footprint. The old
            layout rendered both a 28×28 avatar AND a 14×14 globe
            mark, which doubled up the icon column and left the globe
            looking shrunken next to creator-uploaded artwork. Other
            kinds keep their two-icon stack so the protocol brand mark
            (Flaunch / Uniswap / Aerodrome) remains identifiable next
            to creator artwork. */}
        {adapter.kind === 'custom' ? (
          <CustomAdapterAvatar imageUrl={promo?.imageUrl} brand={brand} />
        ) : (
          <>
            {promo?.imageUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={promo.imageUrl}
                alt=""
                className="w-7 h-7 rounded-[2px] object-cover shrink-0 border border-white/12"
              />
            ) : null}
            <AdapterIcon kind={adapter.kind} brand={brand} />
          </>
        )}
        <span className="text-[11px] text-amber-100 font-medium truncate">
          {headline}
        </span>
        <StatusPill status={adapter.status} />
      </header>

      {adapter.subtitle && (
        <div className="px-2.5 pt-1 -mb-0.5 text-[9px] font-mono tracking-[0.14em] uppercase text-zinc-500 truncate">
          {adapter.subtitle}
        </div>
      )}

      {promo?.description && (
        <div className="px-2.5 py-1 text-[10px] leading-snug text-zinc-200/90 border-b border-amber-500/8">
          {promo.description}
        </div>
      )}

      <div className="px-2.5 py-1.5">{children}</div>

      {belowStats ? (
        <div className="px-2.5 pb-1.5 -mt-0.5 pt-0">{belowStats}</div>
      ) : null}

      {outboundLinks.length > 0 && (
        <div className="px-2.5 pb-1.5 flex items-center gap-1 flex-wrap">
          {outboundLinks.map(({ link, isMetadata }) => (
            <LinkChip
              key={`${link.href}-${link.label}`}
              link={link}
              isMetadata={isMetadata}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 text-[9px] font-mono tracking-[0.12em] uppercase">
      {children}
    </div>
  );
}

export function StatGrid2({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] font-mono tracking-[0.12em] uppercase">
      {children}
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  positive,
  href,
}: {
  label: string;
  value: string;
  hint?: string;
  positive?: boolean;
  /** When set, the value is rendered as an external link to `href` with
   *  a trailing arrow icon. Used by the Custom adapter panel so the
   *  contract address links straight to the block explorer in a new
   *  tab — see `CustomAdapterPanel`. */
  href?: string;
}) {
  return (
    <div className="min-w-0 leading-tight">
      <div className="text-zinc-500">{label}</div>
      {href ? (
        <StatExternalValue href={href} value={value} positive={positive} />
      ) : (
        <div
          className={cn(
            'text-[10.5px] tabular-nums truncate',
            positive ? 'text-emerald-300' : 'text-zinc-100',
          )}
        >
          {value}
        </div>
      )}
      {hint && <div className="text-[8.5px] text-zinc-500 truncate">{hint}</div>}
    </div>
  );
}

function StatExternalValue({
  href,
  value,
  positive,
}: {
  href: string;
  value: string;
  positive?: boolean;
}) {
  // Reuse the same DYOR interstitial guard the LinkChip row uses so
  // adapter contract links route through `useExternalLinkGuard`. Block
  // explorers (basescan / etherscan) are in the trusted-host list and
  // skip the dialog automatically — middle-click / right-click-copy
  // still work because the underlying `<a>` keeps its real `href`.
  const { guardClick } = useExternalLinkGuard();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={guardClick(href)}
      className={cn(
        'inline-flex items-center gap-1 max-w-full text-[10.5px] tabular-nums truncate',
        'hover:text-amber-100 hover:underline underline-offset-2 decoration-amber-400/60',
        positive ? 'text-emerald-300' : 'text-zinc-100',
      )}
    >
      <span className="truncate">{value}</span>
      <ExternalLink
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 opacity-60"
      />
    </a>
  );
}

function StatusPill({ status }: { status: LobbyAdapterStatus }) {
  // "Live" is the default / healthy state — surfacing it adds noise.
  // We only show the pill for actionable states (Paused, Cooldown) where
  // the user needs to know yield is interrupted.
  if (status === 'live') return null;
  const { label, hue } = STATUS_PILL[status];
  return (
    <span
      className="inline-flex items-center gap-1 h-3.5 px-1 rounded-[2px] text-[8.5px] font-mono tracking-[0.18em] uppercase border"
      style={{ color: hue, borderColor: hue + '55', backgroundColor: hue + '14' }}
    >
      <span className="w-1 h-1 rounded-full" style={{ backgroundColor: hue }} />
      {label}
    </span>
  );
}

/**
 * Adapter mark in the card header.
 *
 * Flaunch / Uniswap / Aerodrome render their official brand assets
 * (PNG / ICO from `/public`) so the card carries the protocol's
 * identity rather than the previous stylised SVGs (the FlaunchIcon
 * was a generic rocket silhouette, the UniswapIcon a unicorn-horn —
 * neither read as the actual brand). The brand icon goes flush in
 * a 16×16 box with no tinted background so the protocol logo's own
 * colours can sit directly against the card chrome.
 *
 * Custom adapters don't pass through this helper — they render a
 * single 28×28 avatar slot via {@link CustomAdapterAvatar}, since
 * "custom" has no canonical brand to stand alongside creator artwork.
 */
function AdapterIcon({
  kind,
  brand: _brand,
}: {
  kind: Exclude<LobbyAdapter['kind'], 'custom'>;
  brand: { hue: string; bg: string; border: string };
}) {
  switch (kind) {
    case 'flaunch':
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src="/flaunch-icon.png"
          alt=""
          aria-hidden
          className="w-4 h-4 rounded-[3px] object-contain shrink-0"
        />
      );
    case 'uniswap-v3':
    case 'uniswap-v4':
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src="/icons/uniswap.ico"
          alt=""
          aria-hidden
          className="w-4 h-4 rounded-[3px] object-contain shrink-0"
        />
      );
    case 'aerodrome':
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src="/aero-icon.png"
          alt=""
          aria-hidden
          className="w-4 h-4 rounded-[3px] object-contain shrink-0"
        />
      );
  }
}

/**
 * Single avatar slot for `kind: 'custom'` adapter cards.
 *
 * Renders the creator-uploaded image when one is attached via
 * `pool_metadata.adapter_notes[id].imageUrl`, otherwise falls back
 * to a brand-tinted globe placeholder at the same 28×28 footprint
 * so the card's icon column is consistent regardless of whether the
 * creator has uploaded artwork. The placeholder uses the Custom
 * adapter brand hue (`ADAPTER_BRAND.custom`) so it sits in the same
 * visual family as the left-edge accent stripe.
 */
function CustomAdapterAvatar({
  imageUrl,
  brand,
}: {
  imageUrl: string | undefined;
  brand: { hue: string; bg: string; border: string };
}) {
  if (imageUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={imageUrl}
        alt=""
        className="w-7 h-7 rounded-[2px] object-cover shrink-0 border border-white/12"
      />
    );
  }
  return (
    <span
      aria-hidden
      className="w-7 h-7 rounded-[2px] grid place-items-center shrink-0"
      style={{
        backgroundColor: brand.bg,
        boxShadow: `inset 0 0 0 1px ${brand.border}`,
      }}
    >
      <GlobeIcon size={16} className="text-amber-200" />
    </span>
  );
}

function LinkChip({
  link,
  isMetadata,
}: {
  link: AdapterLink;
  /** True for creator-authored chips from `promo.extraLinks`
   *  (Site / Telegram / Discord). Those route through
   *  `guardMetadataClick` so the DYOR interstitial fires even for
   *  hosts that would normally hit the trusted-host fast-path —
   *  user-authored URLs aren't trustable just because their host is.
   *  False for protocol-built `adapter.links` (basescan / flaunch.gg)
   *  which keep using the host-aware `guardClick`. */
  isMetadata: boolean;
}) {
  // Flaunch's mark is rendered from the brand PNG (same asset used by
  // the panel header `AdapterIcon`) so the chip carries Flaunch's
  // actual logo instead of the generic-rocket SVG fallback that used
  // to fire here. Other protocols keep their inline SVG marks until
  // they get the same brand-asset treatment.
  const Icon =
    link.icon === 'uniswap'
      ? UniswapIcon
      : link.icon === 'dexscreener'
        ? DexscreenerIcon
        : GlobeIcon;
  // Adapter-pool links are third-party by design — route every click
  // through the DYOR guard so users see the destination host before we
  // hand them off. The chip renders `[icon] Label [↗]` so the
  // destination is legible without a hover (the previous icon-only
  // chip leaned on a tooltip that mobile users can't see). Chips were
  // already pruned to one-per-adapter for Flaunch (see grid-to-adapters
  // / mock-adapters) so the wider chip doesn't push the row off a
  // narrow yield column. Other adapter kinds keep their slim outbound
  // sets so the wrap container handles overflow gracefully.
  const { guardClick, guardMetadataClick } = useExternalLinkGuard();
  const onClick = isMetadata
    ? guardMetadataClick(link.href)
    : guardClick(link.href);
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      aria-label={link.label}
      title={link.label}
      className="inline-flex items-center gap-1 h-5 px-1.5 rounded-[3px] bg-black/50 border border-white/10 text-zinc-300 hover:text-amber-100 hover:border-amber-400/40 transition-colors"
    >
      {link.icon === 'flaunch' ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src="/flaunch-icon.png"
          alt=""
          aria-hidden
          className="w-2.5 h-2.5 rounded-[2px] object-contain shrink-0"
        />
      ) : (
        <Icon size={10} />
      )}
      <span className="text-[10px] font-mono tracking-[0.12em]">
        {link.label}
      </span>
      <ExternalLink className="h-2 w-2 opacity-60" />
    </a>
  );
}
